/**
 * L1 Phase 3 production stub for `next-intl`. Resolves
 * `useTranslations(namespace)(key)` against the English bundle
 * (default), the Arabic bundle (`?lang=ar`), or the Brazilian
 * Portuguese bundle (`?lang=pt`).
 *
 * Earlier iterations were English-only (the AR/PT bundles were pruned
 * at commits a7475ab + 7c856b5 during the L1 Phase 3 cleanup). This
 * stub re-introduces Arabic + Portuguese via the demo's `?lang=*`
 * query param without resurrecting the full next-intl Provider
 * plumbing — the locale is read once per page via useDemoLocale() and
 * the bundle is selected at lookup time.
 *
 * Locale falls back to "en" on any miss so a stale key during a
 * translation pass renders the English copy instead of the raw
 * dot-path. Better UX than "thesis.headlineLine1" leaking into the
 * audience's screen mid-demo.
 */
import enMessages from "@/messages/en.json";
import arMessages from "@/messages/ar.json";
import ptMessages from "@/messages/pt.json";
import { useDemoLocale, type DemoLocale } from "../lib/useDemoLocale";

type MessageNode = string | { [key: string]: MessageNode };

const BUNDLES: Record<DemoLocale, MessageNode> = {
  en: enMessages as MessageNode,
  ar: arMessages as MessageNode,
  pt: ptMessages as MessageNode,
};

function lookup(root: MessageNode, dotPath: string): string | undefined {
  let cur: MessageNode | undefined = root;
  for (const segment of dotPath.split(".")) {
    if (cur && typeof cur === "object" && segment in cur) {
      cur = cur[segment];
    } else {
      return undefined;
    }
  }
  return typeof cur === "string" ? cur : undefined;
}

export function useTranslations(namespace?: string) {
  const locale = useDemoLocale();
  const primary = BUNDLES[locale];
  const fallbackBundle = BUNDLES.en;
  return (key: string, params?: { fallback?: string } & Record<string, unknown>): string => {
    const dotPath = namespace ? `${namespace}.${key}` : key;
    // Try the active locale first, fall back to English so an AR key
    // gap renders the canonical English instead of the raw dot-path.
    const found = lookup(primary, dotPath) ?? lookup(fallbackBundle, dotPath);
    if (typeof found === "string") {
      if (params) {
        return found.replace(/\{(\w+)\}/g, (raw, name: string) => {
          const v = (params as Record<string, unknown>)[name];
          return v === undefined ? raw : String(v);
        });
      }
      return found;
    }
    return params?.fallback ?? key;
  };
}

export function useLocale(): string {
  return useDemoLocale();
}

export function useMessages() {
  return BUNDLES[useDemoLocale()];
}

export function NextIntlClientProvider({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}
